import { container } from '@/app/container';
import { IDENTITY_ALERT_ROLES } from '@/shared/guards/roles';
import type {
  PendingAlertItem,
  PendingAlertStatus,
  PendingSource,
} from '@/modules/pending-alerts/domain/entities/pending-alert.entity';
import {
  IDENTITY_OPERATIONAL_ALERT_ENTITY_TYPE,
  type IdentityOperationalAlert,
} from '../domain/entities/user.entity';
import { UsersRepositoryToken } from '../di/tokens';

const STATUS_LABEL: Record<string, string> = {
  OPEN: 'Aberto',
  RESOLVED: 'Resolvido',
  DISMISSED: 'Descartado',
};

/**
 * Rótulos do vocabulário de alerta do Identity (`IdentityAlertType` do
 * monolith). Tabela fixa, não humanização de snake_case: aqui o contrato é
 * conhecido e o texto precisa dizer ao operador **o que fazer** — "conta
 * divergindo do provedor" é acionável, "auth provider intent unreconciled" não.
 */
const TYPE_LABEL: Record<string, string> = {
  auth_provider_intent_unreconciled:
    'Conta possivelmente divergindo do provedor de autenticação',
  duplicate_document: 'Mesmo CPF/CNPJ em mais de uma pessoa',
};

/** `IdentityEntityType` do monolith — só os valores que um alerta referencia. */
const ENTITY_TYPE_LABEL: Record<string, string> = {
  AuthProviderIntent: 'Escrita no provedor de autenticação',
  Account: 'Conta',
  User: 'Pessoa',
};

/** `IdentityAuditEventType` do monolith, no recorte que um alerta gera. */
const EVENT_LABEL: Record<string, string> = {
  ALERT_OPENED: 'Alerta aberto',
  ALERT_RESOLVED: 'Alerta resolvido',
  ALERT_DISMISSED: 'Alerta descartado',
};

const userHref = (userId: string) => `/management/users/${userId}`;

/**
 * O `duplicate_document` aponta para um grupo, não para uma pessoa: o
 * `entityId` é só o menor `userId` do grupo (grão do dedupe no monolith), e
 * quem o operador precisa abrir são todas as pessoas em `details.userIds`. O
 * documento vem oculto (`hiddenDocument`), lido na hora pelo back — o alerta
 * não o grava.
 */
function duplicateDocumentDetails(
  alert: IdentityOperationalAlert,
): Pick<PendingAlertItem, 'detailRows' | 'detailsSummary' | 'detailsNote'> {
  if (alert.type !== 'duplicate_document') return {};
  const userIds = Array.isArray(alert.details.userIds)
    ? (alert.details.userIds as string[])
    : [];
  return {
    detailsSummary: `Ver as ${userIds.length} pessoas com o mesmo documento`,
    detailsNote: alert.hiddenDocument
      ? `O documento ${alert.hiddenDocument} está em mais de uma pessoa:`
      : 'Ninguém do grupo divide mais o documento — o cadastro já foi corrigido.',
    detailRows: userIds.map((userId, i) => ({
      label: `Pessoa ${i + 1}`,
      value: userId,
      href: userHref(userId),
    })),
  };
}

/**
 * Fonte de pendências do Identity para a Central de Pendências
 * (`modules/pending-alerts`). Consome `GET /admin/identity/operational-alerts`
 * (fase B5 de `primum-core-root/docs/plans/done/dual-write-outbox.md`), filtrado em
 * `OPEN` — maiúsculo, `OperationalAlertStatus` do shared, não o vocabulário
 * minúsculo do Sales.
 *
 * Dois gatilhos: o mesmo CPF/CNPJ em mais de uma pessoa
 * (`duplicateDocumentDetails`), e a linha de intenção que o varredor não
 * conseguiu fechar: a conta pode estar com e-mail de login ou senha divergindo do
 * provedor de autenticação, e ninguém automático vai mais tentar. Fechar o
 * alerta é afirmar que a conta foi conferida contra o provedor — a fila das
 * demais linhas mortas é a coleção `identity_auth_intents`, não esta tela.
 */
export const identityPendingSource: PendingSource = {
  key: 'identity',
  label: 'Contas',
  requiredRoles: IDENTITY_ALERT_ROLES,
  async fetch({ page, perPage, type }) {
    const repository = container.resolve(UsersRepositoryToken);
    const response = await repository.listOperationalAlerts({
      page,
      perPage,
      status: 'OPEN',
      type: type || undefined,
      order: 'asc',
    });
    return {
      items: response.items.map((alert) => ({
        id: alert.id,
        sourceLabel: 'Contas',
        // O feed agregado é minúsculo (vocabulário do Sales); o alerta resolve
        // maiúsculo (shared) — normaliza aqui, só para a exibição combinada.
        status: alert.status.toLowerCase() as PendingAlertStatus,
        statusLabel: STATUS_LABEL[alert.status] ?? alert.status,
        typeLabel: TYPE_LABEL[alert.type] ?? alert.type,
        entityTypeLabel:
          ENTITY_TYPE_LABEL[alert.entityType] ?? alert.entityType,
        entityId: alert.entityId,
        entityHref:
          alert.entityType === 'User' ? userHref(alert.entityId) : undefined,
        inThisStatusSince: alert.inThisStatusSince,
        ...duplicateDocumentDetails(alert),
      })),
      total: response.total,
    };
  },
  async fetchFacets() {
    const repository = container.resolve(UsersRepositoryToken);
    const facets = await repository.listOperationalAlertFacets('OPEN');
    return facets.map(({ value, count }) => ({
      value,
      label: TYPE_LABEL[value] ?? value,
      count,
    }));
  },
  actions: {
    writeRoles: IDENTITY_ALERT_ROLES,
    async close(id, resolved, input) {
      const repository = container.resolve(UsersRepositoryToken);
      if (resolved) {
        await repository.resolveOperationalAlert(id, input);
      } else {
        await repository.dismissOperationalAlert(id, input);
      }
    },
    // Sem `uploadAttachment`: o Identity não tem endpoint de upload de anexo
    // (`POST admin/sales/attachments` é exclusivo do Sales) — a UI de anexo
    // some para esta fonte em vez de oferecer um campo que sempre falharia.
    async fetchHistory(id, { page, perPage }) {
      const repository = container.resolve(UsersRepositoryToken);
      const response = await repository.getEntityEvents(
        IDENTITY_OPERATIONAL_ALERT_ENTITY_TYPE,
        id,
        { page, perPage },
      );
      return {
        items: response.items.map((event) => ({
          id: event.id,
          eventLabel: EVENT_LABEL[event.eventType] ?? event.eventType,
          isSystem: event.actorPlatformProfileId === null,
          // O histórico do Identity (`IdentityEntityEventDto`) não resolve o
          // nome do ator como o do TOTVS/Sales faz — só o
          // `actorPlatformProfileId`. Exibir o id não ajudaria; o `reason`
          // (a justificativa do fechamento) é o que o operador precisa ler.
          actorName: null,
          reason: event.reason,
          createdAt: event.createdAt,
          payload: event.payload,
        })),
        total: response.total,
      };
    },
  },
};
